import type { CcpSectionKey } from "./ccp-constants"
import type { CcpPlan, FieldCitation, FieldValue, PlanSource } from "../../types"

import { CCP_POTENTIAL_LINES } from "./ccp-constants"
import { FieldValueView } from "../plan/plan-field"

type OnCitationClick = (citation: FieldCitation) => void

function SubField({
  label,
  field,
  onCitationClick,
  sources,
}: {
  label: string
  field: FieldValue
  onCitationClick?: OnCitationClick
  sources?: PlanSource[]
}) {
  return (
    <div className="border-border/60 border-b py-2 last:border-b-0">
      <p className="text-muted-foreground mb-1 text-xs font-medium">{label}</p>
      <FieldValueView field={field} sources={sources} onCitationClick={onCitationClick} />
    </div>
  )
}

/** Renders the inner content for a single CCP section (composite sections included). */
export function CcpSectionBody({
  sectionKey,
  plan,
  onCitationClick,
  sources,
}: {
  sectionKey: CcpSectionKey
  plan: CcpPlan
  onCitationClick?: OnCitationClick
  sources?: PlanSource[]
}) {
  const s = plan.sections
  switch (sectionKey) {
    case "overview":
    case "market":
    case "players":
    case "model":
    case "needs":
    case "value":
    case "activities":
      return (
        <FieldValueView field={s[sectionKey]} sources={sources} onCitationClick={onCitationClick} />
      )
    case "buying":
      return (
        <div className="space-y-3">
          <FieldValueView
            field={s.buying.note}
            sources={sources}
            onCitationClick={onCitationClick}
          />
          {Object.keys(s.buying.roles).length > 0 ? (
            <div className="border-border/60 border-t pt-2">
              {Object.entries(s.buying.roles).map(([role, field]) => (
                <SubField
                  key={role}
                  field={field}
                  label={role}
                  sources={sources}
                  onCitationClick={onCitationClick}
                />
              ))}
            </div>
          ) : null}
        </div>
      )
    case "potential":
      return (
        <div>
          {CCP_POTENTIAL_LINES.map((line) => (
            <SubField
              key={line.key}
              field={s.potential[line.key]}
              label={line.label}
              sources={sources}
              onCitationClick={onCitationClick}
            />
          ))}
        </div>
      )
    case "engage":
      return (
        <div className="space-y-3">
          <FieldValueView
            field={s.engage.note}
            sources={sources}
            onCitationClick={onCitationClick}
          />
          {s.engage.items.length > 0 ? (
            <div className="border-border/60 border-t pt-2">
              {s.engage.items.map((item, i) => (
                <SubField
                  key={`${item.label}-${i}`}
                  field={item.value}
                  label={item.label}
                  sources={sources}
                  onCitationClick={onCitationClick}
                />
              ))}
            </div>
          ) : null}
          {s.engage.extra ? (
            <FieldValueView
              field={s.engage.extra}
              sources={sources}
              onCitationClick={onCitationClick}
            />
          ) : null}
        </div>
      )
    default: {
      const _exhaustive: never = sectionKey
      return _exhaustive
    }
  }
}
